import React, { useCallback, useEffect } from 'react';
import { DNABox, Iffy, util } from '@alucio/lux-ui';
import useFixedHeader from '@alucio/lux-ui/src/hooks/useFixedLayout';
import { DNAFolderGridList, TitleBar } from 'src/components/DNA';
import { useAllCombinedFolders, getRootFolderORM } from 'src/state/redux/selector/folder';
import folderQuery from 'src/state/redux/folder/query';
import useDisplayMode, { DisplayModes } from 'src/components/DNA/hooks/useDisplayMode';
import { EmptyVariant } from 'src/components/DNA/Empty/DNAEmpty';
import DNAGrid from 'src/components/DNA/Grid/DNAGrid';
import { FolderORM, FolderItemORM, DocumentORM, isFolderORM, isDocumentORM } from 'src/types/types';
import useFolderNavURL from 'src/components/DNA/Folder/Nav/useFolderNavURL';
import { ROUTES } from 'src/router/routes';
import { useDNADocumentActions } from 'src/components/DNA/Document/DNADocument.actions';
import { useDispatch } from 'src/state/redux';
import { sharedFolderActions, useFetchSharedFolders } from 'src/state/redux/slice/sharedFolder';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { useAppSettings } from 'src/state/context/AppSettings';

import { FolderVariants } from './constants';

const FAVORITE_FOLDERS_QUERY = folderQuery.merge(
  folderQuery.filters.pinned,
  folderQuery.filters.rootFolder,
  folderQuery.filters.active,
  folderQuery.sorts.updatedAtDesc,
);

const DNAFavoriteFolders: React.FC = () => {
  const { displayMode, toggleDisplayMode } = useDisplayMode(DisplayModes.list, { manageURL: true });
  const pinnedFolders = useAllCombinedFolders(FAVORITE_FOLDERS_QUERY);
  const fixedHeaderWidth = useFixedHeader();
  const folderNavURL = useFolderNavURL(ROUTES.FOLDERS.PATH);
  const sharedFolderNavURL = useFolderNavURL(ROUTES.SHARED_FOLDERS.PATH);
  const documentActions = useDNADocumentActions();
  const dispatch = useDispatch();
  const { isOnline } = useAppSettings();
  const enableSharedFolders = useFeatureFlags('enableSharedFolders');
  const { fetchSharedFolders } = useFetchSharedFolders();

  useEffect(() => {
    if (enableSharedFolders && isOnline) {
      fetchSharedFolders();
    }
  }, [isOnline, enableSharedFolders]);

  const onPress = useCallback((selectedItemORM: FolderItemORM | FolderORM | DocumentORM) => {
    if (isFolderORM(selectedItemORM)) {
      const {
        model: { id: selectedFolderID },
        relations: { parentFolderORM },
        meta: { hasExternalDependencies, isSharedWithTheUser },
      } = selectedItemORM;

      const rootFolder = parentFolderORM
        ? getRootFolderORM(parentFolderORM)
        : selectedItemORM;
      const rootFolderID = rootFolder?.model?.id;

      rootFolderID && hasExternalDependencies &&
        dispatch(sharedFolderActions.getFolderExternalDependencies({
          folderId: selectedFolderID,
          parentFolderId: rootFolderID,
        }));

      const navURL = isSharedWithTheUser ? sharedFolderNavURL : folderNavURL;
      navURL.pushFolder(selectedItemORM);
    } else if (isDocumentORM(selectedItemORM)) {
      documentActions.preview(selectedItemORM)();
    }
  }, [folderNavURL, sharedFolderNavURL, documentActions, dispatch]);

  return (
    <DNABox
      fill
      appearance="col"
      spacing="md"
      style={util.mergeStyles(
        undefined,
        [{ maxWidth: fixedHeaderWidth }, displayMode === DisplayModes.grid],
      )}
    >
      {/* Header */}
      <DNABox fill style={{ maxWidth: fixedHeaderWidth }}>
        <TitleBar
          title="Favorite Folders"
          testID="page-title"
          description="Browse your favorite folders"
          showToggleView
          toggleView={{
            activeView: displayMode,
            onToggle: toggleDisplayMode,
          }}
        />
      </DNABox>

      {/* Content */}
      <Iffy is={displayMode === DisplayModes.list}>
        <DNAFolderGridList
          variant={FolderVariants.FAVORITES}
          items={pinnedFolders}
          emptyVariant={EmptyVariant.NoFavoriteFoldersAvailable}
          onPress={onPress}
        />
      </Iffy>
      <Iffy is={displayMode === DisplayModes.grid}>
        <DNABox style={{ padding: 16 }}>
          <DNAGrid
            items={pinnedFolders}
            emptyVariant={EmptyVariant.NoFavoriteFoldersAvailable}
            onPress={onPress}
          />
        </DNABox>
      </Iffy>
    </DNABox>
  );
};

export default DNAFavoriteFolders;
